import * as XLSX from 'xlsx'
import { ElMessage } from 'element-plus'

/**
 * Export data to Excel (XLSX) format
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the exported file (without extension)
 * @param {String} sheetName - Name of the worksheet (default: 'Sheet1')
 * @param {Boolean} autoFitColumns - Auto-size columns based on content (default: true)
 */
export function exportToExcel(data, filename = 'export', sheetName = 'Sheet1', autoFitColumns = true) {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      ElMessage.warning('No data to export')
      return false
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(data)

    // Auto-size columns if enabled
    if (autoFitColumns) {
      const colWidths = Object.keys(data[0]).map(key => ({
        wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length)) + 2
      }))
      ws['!cols'] = colWidths
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName)

    // Generate filename with timestamp if not provided
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19).replace('T', '_')
    const finalFilename = filename.includes('.xlsx') ? filename : `${filename}_${timestamp}.xlsx`

    // Write and download file
    XLSX.writeFile(wb, finalFilename)

    return true
  } catch (error) {
    console.error('Excel export failed:', error)
    ElMessage.error('Export failed: ' + error.message)
    return false
  }
}

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the exported file (without extension)
 * @param {String} delimiter - CSV delimiter (default: ',')
 */
export function exportToCSV(data, filename = 'export', delimiter = ',') {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      ElMessage.warning('No data to export')
      return false
    }

    // Get headers from first object
    const headers = Object.keys(data[0])
    
    // Create CSV content
    const csvContent = [
      // Header row
      headers.join(delimiter),
      // Data rows
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] ?? ''
          // Escape quotes and wrap in quotes if contains delimiter or newline
          const stringValue = String(value)
          if (stringValue.includes(delimiter) || stringValue.includes('\n') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`
          }
          return stringValue
        }).join(delimiter)
      )
    ].join('\n')

    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // Generate filename with timestamp if not provided
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19).replace('T', '_')
    const finalFilename = filename.includes('.csv') ? filename : `${filename}_${timestamp}.csv`
    
    link.setAttribute('download', finalFilename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return true
  } catch (error) {
    console.error('CSV export failed:', error)
    ElMessage.error('Export failed: ' + error.message)
    return false
  }
}

/**
 * Transform API response and export to Excel
 * @param {Object} response - API response object
 * @param {Function} transformFn - Function to transform data items
 * @param {String} filename - Name of the exported file
 * @param {String} sheetName - Name of the worksheet
 */
export async function exportApiResponse(response, transformFn, filename = 'export', sheetName = 'Data') {
  try {
    if (!response?.success) {
      ElMessage.error(response?.message || 'Failed to fetch data for export')
      return false
    }

    const rawData = response.data?.list || response.data || []
    
    if (!rawData || rawData.length === 0) {
      ElMessage.warning('No data available to export')
      return false
    }

    // Transform data if transform function provided
    const exportData = transformFn ? rawData.map(transformFn) : rawData

    return exportToExcel(exportData, filename, sheetName)
  } catch (error) {
    console.error('Export API response failed:', error)
    ElMessage.error('Export failed: ' + error.message)
    return false
  }
}

/**
 * Create export handler for Vue components
 * @param {Function} fetchFn - Async function to fetch data
 * @param {Function} transformFn - Function to transform data items for export
 * @param {String} filename - Base filename for export
 * @param {String} format - Export format: 'xlsx' or 'csv' (default: 'xlsx')
 * @returns {Function} - Export handler function
 */
export function createExportHandler(fetchFn, transformFn, filename = 'export', format = 'xlsx') {
  return async (...args) => {
    try {
      // Show loading state if needed
      const response = await fetchFn(...args)
      
      if (!response?.success) {
        ElMessage.error(response?.message || 'Failed to fetch data')
        return false
      }

      const rawData = response.data?.list || response.data || []
      
      if (!rawData || rawData.length === 0) {
        ElMessage.warning('No data available to export')
        return false
      }

      // Transform data
      const exportData = transformFn ? rawData.map(transformFn) : rawData

      // Export based on format
      if (format === 'csv') {
        return exportToCSV(exportData, filename)
      } else {
        return exportToExcel(exportData, filename)
      }
    } catch (error) {
      console.error('Export handler failed:', error)
      ElMessage.error('Export failed: ' + error.message)
      return false
    }
  }
}
